import MuiBox from '@mui/material/Box';
import type { ReactElement, ReactNode } from 'react';
import { BrandLockup } from '../BrandLockup';

// Pinned, not a minimum: a band that changes height reads as the page moving. Exported because a
// surface that lays anything against the band (a tab inset, a sticky offset) must measure from this
// number rather than restate it.
export const SURFACE_HEADER_HEIGHT = 68;

export interface SurfaceHeaderProps {
  // The market indicator text («Soporte ES»), resolved by the host.
  brandLabel: string;
  // The market could not be resolved reliably — the pill takes the warning treatment.
  brandDegraded: boolean;
  // The centre zone, right after the lockup — where a surface puts its own navigation (tabs).
  children?: ReactNode;
  // The host's own chrome at the far edge: the user menu, and with it the way to sign out.
  end?: ReactNode;
}

// The chrome band every top-level surface wears: lockup, optional centre zone, the host's end slot.
// It started duplicated per surface on purpose (two surfaces were an observation, not a pattern);
// the third surface made it one, so its measures now live here and no surface can drift from them.
// No `sx`: fixed chrome whose whole point is that no caller restyles it.
export function SurfaceHeader({
  brandLabel,
  brandDegraded,
  children,
  end,
}: SurfaceHeaderProps): ReactElement {
  return (
    <MuiBox
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        height: SURFACE_HEADER_HEIGHT,
        borderBottom: 1,
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        // White chrome over the surface gray the content sits on.
        bgcolor: 'background.paper',
        // The AppBar layer: content scrolling under the band never paints over it.
        zIndex: 1100,
      }}
    >
      <BrandLockup label={brandLabel} degraded={brandDegraded} />
      {children}
      <MuiBox sx={{ flexGrow: 1 }} />
      {end}
    </MuiBox>
  );
}
